import React, { useState } from 'react';
import {LoadingButton} from '@skyline/react-core/components/LoadingButton/LoadingButton';

import {openPopupWindow} from '../../../../services/popupWindow/openPopupWindow';
import {createCheckoutSessionFromCommonCheckoutApi} from '../../../../services/commonCheckout/createCheckoutSessionFromCommonCheckoutApi';
import {httpClientService} from '../../../../services/settingsApi/httpClientService';
import {settingsApi} from '../../../../services/settingsApi/settingsApi';
import {APP_ENV} from '../../../../utils/constants';
import {logPaymentStatusEvent} from '../../../../services/AppInsights/Events/log-payment-status-event';
import {appInsights} from '../../../../services/AppInsights/app-insights';

const getCodeFromExceptionOrDefault = (error, defaultValue = 'INITIALIZATION_ERROR') => {
  return error?.response?.data?.title || defaultValue;
};

const getDescriptionFromExceptionOrDefault = (error, defaultValue = '') => {
  return error?.response?.data?.detail || defaultValue;
};

export const PaymentButton: React.FC<PaymentButtonProps> = ({
  appEnv = APP_ENV.stage,
  onSuccess,
  onFailure,
  onCancel,
  // checkoutType,
  // currentCheckoutType,
  onInitializeError,
  amount = 0,
  hsid,
  phoneNumber,
  merchantId,
  merchantTransactionId,
  children,
  metadata,
  // setCurrentCheckoutType,
  buttonColor = 'primary',
  statementDescriptorSuffix = '',
}) => {
  // const isGuest = checkoutType === 'guest';
  const CHECKOUT_TYPES = {
    GUEST_PAY: "guest",
    WALLET_PAY: "wallet",
    ADD_CARD: "card"
  }

  const [isLoading, setIsLoading] = useState(false);

  // TODO: check HSID validity before trusting
  let checkoutType = !!hsid && amount > 0 ? CHECKOUT_TYPES.WALLET_PAY : (amount > 0 ? CHECKOUT_TYPES.GUEST_PAY : CHECKOUT_TYPES.ADD_CARD);

  const handlePaymentSuccess = (checkoutSessionId: string, result: CheckoutSessionsResponse) => {
    logPaymentStatusEvent({
      checkoutSessionId,
      // checkoutType,
      paymentStatus: 'Success',
      customerId: result.checkoutSession.customerId,
    });

    if (onSuccess) {
      onSuccess(result);
    }
  };

  const handlePaymentFailed = (
    checkoutSessionId: string,
    result: CheckoutSessionsResponse,
    httpError: HTTPErrorResponse
  ) => {
    logPaymentStatusEvent({
      checkoutSessionId,
      // checkoutType,
      paymentStatus: 'Failed',
      customerId: result?.checkoutSession?.customerId,
    });

    const failureResult: CheckoutSessionsResponse = {
      ...result,
    };

    failureResult.checkoutSession.error = {
      errorGroup: httpError.title,
      errorCode: httpError.title,
      message: httpError.detail,
      httpStatus: httpError.status?.toString(),
    };

    delete failureResult.hcpToken;

    if (onFailure) {
      onFailure(failureResult);
    }
  };

  const handlePaymentCancelled = (checkoutSessionId: string, result?: CheckoutSessionsResponse) => {
    logPaymentStatusEvent({
      checkoutSessionId,
      // checkoutType,
      paymentStatus: 'Cancelled',
      customerId: result?.checkoutSession?.customerId,
    });

    if (onCancel) {
      onCancel(result);
    }
  };

  // on confirming payment - pay button click
  const handleSubmit = () => {
    setIsLoading(true);
    if (!merchantId || !phoneNumber || phoneNumber.length < 10) {
      const errorMessage = `${!merchantId ? 'Merchant ID is required' : 'Phone Number is invalid'}`;
      console.error(errorMessage);
      setIsLoading(false);
      onInitializeError?.({code: 'INITIALIZATION_ERROR', description: errorMessage});
      return;
    }

    // const width = 400;
    // const height = 850;
    // const left = window.screen.width / 2 - width / 2;
    // const win = window.open('', '_blank', `popup=true,width=${width},height=${height},left=${left}`);

    // setCurrentCheckoutType(checkoutType);
    httpClientService.init({appEnv});

    (async () => {
      const popupUrl = await settingsApi.getWalletUrl();
      const aiConnectionString = await settingsApi.getAIConnectionString();

      appInsights.init(aiConnectionString);

      const promiseResponse = await createCheckoutSessionFromCommonCheckoutApi({
        merchantId,
        merchantTransactionId,
        amount,
        hsid,
        phoneNumber,
        statementDescriptorSuffix,
        metadata,
      }).catch((error) => {
        // if (win) {
        //   win.close();
        // }

        // setCurrentCheckoutType(null);
        setIsLoading(false);
        onInitializeError?.({
          code: getCodeFromExceptionOrDefault(error),
          description: getDescriptionFromExceptionOrDefault(error),
        });
      });

      const response = promiseResponse?.response;
      const hcpToken = promiseResponse?.hcpToken;
      const checkoutSessionId = promiseResponse?.checkoutSessionId;
      const checkStatus = promiseResponse?.checkStatus;

      if (!response || !hcpToken || !checkoutSessionId || !checkStatus) {
        setIsLoading(false);
        return;
      }

      const urlParams = `?checkoutId=${encodeURIComponent(checkoutSessionId)}&appEnv=${appEnv}`;
      /*&hcpToken=${encodeURIComponent(
        hcpToken
      )};*/
      // const urlPath = isGuest ? '/guest' : '/secure';
      let urlPath = '/guest';
      switch (checkoutType){
        case CHECKOUT_TYPES.WALLET_PAY: 
          urlPath = '/pay';
        break;
        case CHECKOUT_TYPES.ADD_CARD: 
          urlPath = '/card';
        break;
        default: 
          urlPath = '/guest';
      }
      const url = `${popupUrl}${urlPath}${urlParams}`;
      const checkoutSessionsResponse: CheckoutSessionsResponse = response.data as CheckoutSessionsResponse;
      const params = {
        url,
        checkoutSessionId,
        checkoutSessionsResponse,
        onSuccess: handlePaymentSuccess,
        onFailure: handlePaymentFailed,
        onCancel: handlePaymentCancelled,
        checkStatus,
      };

      const [promise] = openPopupWindow(params, null);
      promise
        ?.then(() => {
          setIsLoading(false);
          // setCurrentCheckoutType(null);
        })
        .catch((error) => {
          setIsLoading(false);
          // setCurrentCheckoutType(null);
          onInitializeError?.({
            code: getCodeFromExceptionOrDefault(error),
            description: getDescriptionFromExceptionOrDefault(error),
          });
          console.error(`Error in the payment window: ${error}`);
        });
    })().catch((error) => {
      // setCurrentCheckoutType(null);
      setIsLoading(false);
      onInitializeError?.({
        code: getCodeFromExceptionOrDefault(error),
        description: getDescriptionFromExceptionOrDefault(error),
      });
      console.error(`Error during opening the payment window: ${error}`);
    });
  };

  return (
    <LoadingButton
      color={buttonColor}
      variant="contained"
      fullWidth
      onClick={handleSubmit}
      isLoading={!!isLoading}
      disabled={!!isLoading}
    >
      {children}
    </LoadingButton>
  );
};
